import { useRef, useState } from "react";
import axios from "../../services/axios/backendAxios";
// import { User } from "../../types";
import SubtleButton from "../../components/Buttons/SubtleButton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import flash from "../../services/flash";
import errors from "../../services/errors";
import { UserSelect } from "./TransferOwnership";
import { Account, User } from "@mysnowscape/snow-lib";
import { useDispatch } from "react-redux";
import { updateUser } from "../../store/reducers/currentUserSlice";
type FormPayload = {
  user_id: string;
};
export default function UserInfo() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const allowedDevicesDebounce = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const accountsQuery = useQuery<Account[]>({
    queryKey: ["account", `user-${selectedUser?._id}`],
    queryFn: async () => {
      const resp = await axios.get(
        "/account?ownerId=" + selectedUser?._id.toString(),
      );
      return resp.data.accounts;
    },
    enabled: selectedUser != null,
  });
  const updateStripeInfo = useMutation({
    mutationFn: (data: FormPayload) => {
      return axios.post(`user/developer/update-stripe-info`, data);
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? "Stripe info updated.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (key: any) =>
              typeof key === "string" &&
              (key.includes("company") ||
                key.includes("companies") ||
                key.includes("in-good-standing") ||
                key.includes("account") ||
                key.includes("user")),
          ) !== undefined,
      });
    },
    onError: (err) => {
      flash.error("Error: " + (err.message ?? err));
      errors.report(err);
    },
  });
  const unlockAccount = useMutation({
    mutationFn: (accountId: string) => {
      return axios.post(`account/${accountId}/unlock`);
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? "Account unlocked.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (key: any) =>
              key.includes("company") ||
              key.includes("companies") ||
              key.includes("in-good-standing") ||
              key.includes("account") ||
              key.includes("user"),
          ) !== undefined,
      });
    },
    onError: (err) => {
      flash.error("Error: " + (err.message ?? err));
      errors.report(err);
    },
  });
  const updateAllowedDevices = useMutation({
    mutationFn: (allowedDevices: number) => {
      return axios.post(`user/${selectedUser!.uid}/update`, { allowedDevices });
    },
    onSuccess: (resp) => {
      setSelectedUser(resp.data);
      flash.success("Allowed devices updated.");
    },
    onError: (err) => {
      flash.error("Error: " + (err.message ?? err));
      errors.report(err);
    },
  });

  const impersonate = async (phoneNumber: string) => {
    try {
      await axios.post("/user/impersonate", { phone: phoneNumber });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.response?.data?.message) {
        flash.error(err.response.data.message);
      }
    }
    let userResponse = await axios.get("/user/me");
    await dispatch({ type: "SIGNOUT_CLEAR_STORE" });
    await dispatch(
      updateUser({
        ...userResponse.data.user,
        is_impersonating: userResponse.data.is_impersonating ?? false,
      }),
    );
    // window.location.reload();
  };

  return (
    <div>
      <h2 className="text-lg font-bold">User / Account Tools</h2>
      <p className="text-xs text-gray-700">
        Select a user to see their details, log in as them, remove account lock,
        or update their subscription info from stripe.
      </p>
      <form className="grid grid-cols-1 gap-2">
        <label>
          <span className="text-xs text-gray-500">User</span>
          <UserSelect
            name="user_id"
            onChange={(user) => setSelectedUser(user)}
          />
        </label>
        <h2 className="font-bold">User Details</h2>
        <table>
          <tr>
            <td>Database ID</td>
            <td>{selectedUser?._id.toString()}</td>
          </tr>
          <tr>
            <td>Firebase Id (uid)</td>
            <td>{selectedUser?.uid}</td>
          </tr>
          <tr>
            <td>Email</td>
            <td>{selectedUser?.email}</td>
          </tr>
          <tr>
            <td>Allowed Devices</td>
            <td>
              {selectedUser && (
                <input
                  key={selectedUser?._id?.toString()}
                  type="number"
                  min={1}
                  disabled={
                    selectedUser === null || updateAllowedDevices.isPending
                  }
                  defaultValue={selectedUser?.allowedDevices ?? 1}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (allowedDevicesDebounce.current)
                      clearTimeout(allowedDevicesDebounce.current);
                    allowedDevicesDebounce.current = setTimeout(() => {
                      if (
                        selectedUser &&
                        val >= 1 &&
                        val !== (selectedUser.allowedDevices ?? 1)
                      ) {
                        updateAllowedDevices.mutate(val);
                      }
                    }, 600);
                  }}
                  onBlur={(e) => {
                    if (allowedDevicesDebounce.current)
                      clearTimeout(allowedDevicesDebounce.current);
                    const val = Number(e.target.value);
                    if (
                      selectedUser &&
                      val !== (selectedUser.allowedDevices ?? 1)
                    ) {
                      updateAllowedDevices.mutate(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (allowedDevicesDebounce.current)
                        clearTimeout(allowedDevicesDebounce.current);
                      const val = Number((e.target as HTMLInputElement).value);
                      if (
                        selectedUser &&
                        val !== (selectedUser.allowedDevices ?? 1)
                      ) {
                        updateAllowedDevices.mutate(val);
                      }
                    }
                  }}
                  className="border rounded px-1 w-16 text-sm"
                />
              )}
            </td>
          </tr>
          <tr>
            <td>Error Reports</td>
            <td>
              {selectedUser && (
                <>
                  <a
                    className="underline mr-2"
                    href={`https://app.honeybadger.io/projects/117723/faults?sort=last_seen_desc&q=context.user_id%3A%22${selectedUser?.uid}%22%E2%80%81-is%3Aresolved%E2%80%81-is%3Aignored%E2%80%81`}
                  >
                    Web
                  </a>
                  <a
                    className="underline mr-2"
                    href={`https://app.honeybadger.io/projects/115977/faults?sort=last_seen_desc&q=context.user_id%3A%22${selectedUser?.uid}%22%E2%80%81-is%3Aresolved%E2%80%81-is%3Aignored%E2%80%81`}
                  >
                    Backend
                  </a>
                  <a
                    className="underline mr-2"
                    href={`https://app.honeybadger.io/projects/116062/faults?sort=last_seen_desc&q=context.user_id%3A%22${selectedUser?.uid}%22%E2%80%81-is%3Aresolved%E2%80%81-is%3Aignored%E2%80%81`}
                  >
                    App
                  </a>
                </>
              )}
            </td>
          </tr>
        </table>
        <h2 className="font-bold">User Accounts</h2>
        <table>
          {accountsQuery.data?.map((a) => (
            <tr key={a._id.toString()}>
              <td>{a._id.toString()}</td>
              <td>
                {a.locked ? (
                  <>
                    <div className="text-snow-red">
                      Locked: {a.lockedMessage ?? "No reason given"}
                    </div>
                    <SubtleButton
                      type="button"
                      loading={unlockAccount.isPending}
                      onClick={() => {
                        unlockAccount.mutate(a._id.toString());
                      }}
                    >
                      Unlock
                    </SubtleButton>
                  </>
                ) : (
                  <span>Ok</span>
                )}
              </td>
            </tr>
          ))}
        </table>
        <div className="actions flex gap-2">
          <SubtleButton
            disabled={selectedUser === null}
            type="button"
            className="mt-2"
            loading={updateStripeInfo.isPending}
            onClick={() => {
              updateStripeInfo.mutate({
                user_id: selectedUser!._id,
              });
            }}
          >
            Update Stripe Info
          </SubtleButton>
          <SubtleButton
            disabled={selectedUser === null}
            type="button"
            className="mt-2"
            onClick={() => {
              impersonate(selectedUser!.phoneNumber);
            }}
          >
            Login As User
          </SubtleButton>
        </div>
      </form>
    </div>
  );
}
